<template>
  <div class="section overflow-hidden position-relative min-height" id="wrapper">

    <!-- Page Content Wrapper Start -->
    <div class="page-content-wrapper padding-left-0">
      <!-- First Row: Upload File (Left) and Instructions (Right) -->
      <div class="container-fluid custom-container row gx-10 margin-10">
        <!-- Upload File Section (Left Column) -->
        <div class="upload-modal margin-10">
          <h2>Upload File</h2>

          <!-- Drag and Drop Area -->
          <div
            class="upload-area"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            :class="{ 'drag-over': isDragging }"
          >
            <input
              type="file"
              ref="fileInput"
              accept=".xls,.xlsx"
              @change="handleFileSelect"
              hidden
            />
            <p v-if="!selectedFile">Drag and drop an Excel file here or <span @click="triggerFileInput" class="file-select">Choose a File</span>.</p>
            <p v-if="selectedFile" class="file-name">{{ selectedFile.name }}</p>
            <p class="supported-info">Supported formats: XLS, XLSX &nbsp;&bull;&nbsp; Maximum size: 25MB</p>
          </div>

          <!-- Action Buttons -->
          <div class="actions">
            <button class="cancel" @click="cancelUpload">Cancel</button>
            <button class="next" @click="submitFile" :disabled="!selectedFile">
              <div v-if='loader' class="loader"></div>
              <span v-else>Upload</span>
              
            </button>
          </div>
          <div v-if='message'><p class="error_message">{{message}}</p></div>
          <div v-if="showSuccess" class="popup-overlay">
            <div class="upload-success-popup">
              <p class="popup-message">✅ Excel file uploaded successfully!</p>
              <button class="close-btn" @click="showSuccess = false">OK</button>
            </div>
          </div>
          <br/><br/>
          
          <!-- Example File Download Section -->
          <div class="example-section">
            <img src="/assets/images/excel.png" alt="Excel Icon" class="excel-icon" />
            <span>Sample File</span>
            <button @click="downloadSample">Download</button>
          </div>
        </div>

        <!-- Instructions Section (Right Column) -->
        <div class="upload-modal margin-10">
          <h4>Bulk User Upload Instructions</h4>
          <span>Use this page to grant your team access to the COLREGs portal by uploading their details in bulk. Please follow the steps below carefully:</span><br/><br/>
          <ol>
            <li>1. Click the <b>Download</b> button to get the Excel template.</li>
            <li>2. Open the downloaded template and enter the details of each team member in a separate row. You may add as many rows as needed.</li>
            <li>3. Click <b>Choose a File</b> to upload your filled Excel sheet.</li>
            <li>4. Once the correct file is selected, click the <b>Upload</b> button to begin processing.</li>
            <li>5. After a successful upload, each team member will receive an email with instructions to complete their registration.</li>
            <li>6. You can monitor the registration status of all uploaded users on the <b>Team Members</b> page.</li>
          </ol>
        </div>
      </div>

      <!-- Single User Upload Section (Full Width Below) -->
      <div class="container-fluid custom-container margin-10">
        <div class="upload-modal margin-10" style="width: 100%; max-width: none;">
          <div class="collapsible-header" @click="toggleSingleUserForm">
            <h4>Click here to add a single user</h4>
            <span class="toggle-icon">{{ showSingleUserForm ? '▼' : '▶' }}</span>
          </div>
          
          <transition name="collapse">
            <div v-if="showSingleUserForm" class="single-user-form">
              <p class="form-description">Use this form to add a single user directly without uploading an Excel file.</p>
              
              <div class="form-group">
                <label for="firstName">First Name <span class="required">*</span></label>
                <input 
                  type="text" 
                  id="firstName" 
                  v-model="singleUser.first_name" 
                  placeholder="Enter first name"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label for="lastName">Last Name <span class="required">*</span></label>
                <input 
                  type="text" 
                  id="lastName" 
                  v-model="singleUser.last_name" 
                  placeholder="Enter last name"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label for="email">Email <span class="required">*</span></label>
                <input 
                  type="email" 
                  id="email" 
                  v-model="singleUser.email" 
                  placeholder="Enter email address"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label for="mobile">Mobile <span class="required">*</span></label>
                <input 
                  type="tel" 
                  id="mobile" 
                  v-model="singleUser.mobile" 
                  placeholder="Enter mobile number"
                  class="form-input"
                />
              </div>

              <div v-if='singleUserMessage'><p class="error_message">{{singleUserMessage}}</p></div>

              <div class="actions">
                <button class="cancel" @click="clearSingleUserForm">Clear</button>
                <button class="next" @click="submitSingleUser">
                  <div v-if='singleUserLoader' class="loader"></div>
                  <span v-else>Add User</span>
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Success Popup for Single User -->
      <div v-if="showSingleUserSuccess" class="popup-overlay">
        <div class="upload-success-popup">
          <p class="popup-message">✅ User added successfully!</p>
          <button class="close-btn" @click="showSingleUserSuccess = false">OK</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { uploadExcelFile, uploadSingleUser } from '@/services/bulkUserService';
export default {
  data() {
    return {
      isDragging: false,
      selectedFile: null,
      loader: false,
      message: null,
      showSuccess: false,
      showSingleUserForm: false,
      singleUser: {
        first_name: '',
        last_name: '',
        email: '',
        mobile: ''
      },
      singleUserLoader: false,
      singleUserMessage: null,
      showSingleUserSuccess: false,
    };
  },
  methods: {

    toggleSingleUserForm() {
      this.showSingleUserForm = !this.showSingleUserForm;
    },

    clearSingleUserForm() {
      this.singleUser = {
        first_name: '',
        last_name: '',
        email: '',
        mobile: ''
      };
      this.singleUserMessage = null;
    },

    validateSingleUserForm() {
      if (!this.singleUser.first_name || !this.singleUser.first_name.trim()) {
        this.singleUserMessage = 'First name is required.';
        return false;
      }
      if (!this.singleUser.last_name || !this.singleUser.last_name.trim()) {
        this.singleUserMessage = 'Last name is required.';
        return false;
      }
      if (!this.singleUser.email || !this.singleUser.email.trim()) {
        this.singleUserMessage = 'Email is required.';
        return false;
      }
      
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.singleUser.email)) {
        this.singleUserMessage = 'Please enter a valid email address.';
        return false;
      }

      if (!this.singleUser.mobile || !this.singleUser.mobile.trim()) {
        this.singleUserMessage = 'Mobile number is required.';
        return false;
      }

      return true;
    },

    async submitSingleUser() {
      this.singleUserMessage = null;
      
      if (!this.validateSingleUserForm()) {
        return;
      }

      this.singleUserLoader = true;

      try {
        await uploadSingleUser(this.singleUser);
        this.singleUserMessage = null;
        this.showSingleUserSuccess = true;
        setTimeout(() => {
          this.showSingleUserSuccess = false;
        }, 3000);
        this.clearSingleUserForm();
      } catch (error) {
        this.singleUserMessage = error.response.data.message || error.response.data.error || JSON.stringify(error.response.data);
      } finally {
        this.singleUserLoader = false;
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.validateAndSetFile(files[0]);
      }
    },
    handleDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.validateAndSetFile(files[0]);
      }
    },
    validateAndSetFile(file) {
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      const maxSize = 25 * 1024 * 1024; // 25MB

      if (!allowedTypes.includes(file.type)) {
        this.message = 'Invalid file type. Please upload an XLS or XLSX file.';
        this.selectedFile = null;
        return;
      }

      if (file.size > maxSize) {
        this.message = 'File size exceeds the 25MB limit.';
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
    },
    downloadSample() {
      const link = document.createElement('a');
      link.href = '/sample.xlsx';
      link.download = 'sample.xlsx';
      link.click();
    },
    cancelUpload() {
      this.selectedFile = null;
      this.$refs.fileInput.value = null;
    },
    async submitFile() {
      if (!this.selectedFile) {
        this.message = 'Please select a file first.';
        return;
      }
      this.loader = true;
      const formData = new FormData();
      formData.append('excelFile', this.selectedFile);
      formData.append('user', this.user)

      try {
        await uploadExcelFile(formData);
        this.message = null;
        this.showSuccess = true;
        setTimeout(() => {
          this.showSuccess = false;
        }, 3000);
        this.selectedFile = null;
      } catch (error) {
        this.message = error.response.data.message || error.response.data.error || 'Upload failed: ' + JSON.stringify(error.response.data);
        this.selectedFile = null;
      } finally {
        this.loader = false;
      }
    },
  },
};
</script>

<style scoped>
.loader {
  width: 20px;
  aspect-ratio: 1;
  border-radius: 50%;
  border: 4px solid white;
  border-right-color: #007bff;
  animation: l2 1s infinite linear;
}
@keyframes l2 {to{transform: rotate(1turn)}}
.upload-modal {
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  width: 480px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.upload-modal h2 {
  margin-bottom: 20px;
  font-size: 20px;
}

.upload-area {
  border: 2px dashed #b3d4fc;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  background: #f4f8ff;
  cursor: pointer;
  transition: border-color 0.3s;
}

.upload-area.drag-over {
  border-color: #007bff;
}

.upload-area .file-select {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
}

.upload-area .file-name {
  font-weight: bold;
  color: #007bff;
}

.supported-info {
  font-size: 12px;
  color: #777;
  margin-top: 10px;
}

.example-section {
  display: flex;
  align-items: center;
  margin-top: 20px;
  background: #f1f3f5;
  padding: 12px;
  border-radius: 8px;
}

.example-section .excel-icon {
  width: 24px;
  margin-right: 12px;
}

.example-section span {
  font-size: 14px;
  flex: 1;
}

.example-section button {
  padding: 5px 10px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.actions {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
}

.actions button {
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-weight: bold;
  cursor: pointer;
}

.actions .cancel {
  background: #f1f1f1;
  color: #333;
}

.actions .next {
  background: #007bff;
  color: white;
}

.actions .next:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Collapsible Section Styles */
.collapsible-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 15px 0;
  border-bottom: 2px solid #f1f3f5;
  margin-bottom: 15px;
  user-select: none;
}

.collapsible-header h4 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.toggle-icon {
  color: #007bff;
  font-size: 16px;
  font-weight: bold;
  transition: transform 0.3s;
}

.single-user-form {
  padding-top: 15px;
}

.form-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.required {
  color: #dc3545;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #007bff;
}

.form-input::placeholder {
  color: #999;
}

/* Collapse Animation */
.collapse-enter-active, .collapse-leave-active {
  transition: all 0.3s ease;
  max-height: 600px;
  overflow: hidden;
}

.collapse-enter-from, .collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Dimmed background overlay */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Centered popup */
.upload-success-popup {
  background-color: #d1fae5;
  color: #065f46;
  border: 2px solid #10b981;
  border-radius: 12px;
  padding: 30px 40px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

/* Message text */
.popup-message {
  margin-bottom: 20px;
}

/* Close button */
.close-btn {
  background-color: #10b981;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
}

.close-btn:hover {
  background-color: #059669;
}

.error_message {
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
}

</style>